import { BASE_URL } from '../components/constants';
import { authHeader } from './authHeaders';

// Chunks grow so each one is synthesized while the previous one plays; the small first one starts audio quickly.
const CHUNK_SIZES = [200, 500, 1000, 1500];
const PREFETCH_AHEAD = 2;
const CACHE_LIMIT = 25;

// voice|text -> Promise<objectUrl>, kept across components so replaying an answer is instant
const audioCache = new Map();

function splitSentences(text) {
  const parts = [];
  const re = /[.!?]+\s+|\n+/g;
  let start = 0;
  let m;
  while ((m = re.exec(text))) {
    // Keep "23. Januar" together
    if (m[0][0] === '.' && /\d/.test(text[m.index - 1] || '')) continue;
    const end = m.index + m[0].length;
    parts.push(text.slice(start, end));
    start = end;
  }
  if (start < text.length) parts.push(text.slice(start));
  return parts;
}

export function splitForSpeech(text) {
  const chunks = [];
  let current = '';
  for (const sentence of splitSentences(text)) {
    const limit = CHUNK_SIZES[Math.min(chunks.length, CHUNK_SIZES.length - 1)];
    if (current.trim() && current.length + sentence.length > limit) {
      chunks.push(current.trim());
      current = sentence;
    } else {
      current += sentence;
    }
  }
  if (current.trim()) chunks.push(current.trim());
  return chunks;
}

function fetchChunkAudio(text, voice) {
  const key = `${voice}|${text}`;
  const cached = audioCache.get(key);
  if (cached) {
    audioCache.delete(key);
    audioCache.set(key, cached);
    return cached;
  }

  const promise = fetch(`${BASE_URL}/tts/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeader() },
    body: JSON.stringify({ text, voice }),
  })
    .then((response) => {
      if (!response.ok) throw new Error('TTS request failed');
      return response.blob();
    })
    .then((blob) => URL.createObjectURL(blob));

  promise.catch(() => {
    if (audioCache.get(key) === promise) audioCache.delete(key);
  });
  audioCache.set(key, promise);

  while (audioCache.size > CACHE_LIMIT) {
    const [oldKey, oldPromise] = audioCache.entries().next().value;
    audioCache.delete(oldKey);
    oldPromise.then((url) => URL.revokeObjectURL(url)).catch(() => {});
  }
  return promise;
}

// Plays text chunk by chunk; returns { stop }. Must be called from the click handler (one Audio element for Safari).
export function playSpeech(text, voice, { onPlaying, onEnd, onError } = {}) {
  const chunks = splitForSpeech(text);
  const audio = new Audio();
  let stopped = false;
  let index = 0;

  const finish = (error) => {
    if (stopped) return;
    stopped = true;
    if (error) onError?.(error);
    else onEnd?.();
  };

  const playAt = async (i) => {
    if (stopped) return;
    if (i >= chunks.length) {
      finish();
      return;
    }
    index = i;
    for (let j = i + 1; j <= Math.min(i + PREFETCH_AHEAD, chunks.length - 1); j++) {
      fetchChunkAudio(chunks[j], voice).catch(() => {});
    }
    try {
      const url = await fetchChunkAudio(chunks[i], voice);
      if (stopped) return;
      audio.src = url;
      await audio.play();
      if (stopped) {
        audio.pause();
        return;
      }
      if (i === 0) onPlaying?.();
    } catch (error) {
      finish(error);
    }
  };

  audio.onended = () => playAt(index + 1);
  audio.onerror = () => finish(new Error('Audio playback failed'));

  if (chunks.length === 0) {
    finish();
  } else {
    playAt(0);
  }

  return {
    stop: () => {
      if (stopped) return;
      stopped = true;
      audio.pause();
    },
  };
}
